import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities/api-entitites';
import {
  EntityState,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import {
  GrantProfile,
  GrantProfileCreateRequest,
  GrantProfilePatchRequest,
} from '../grant-profile-types';

const grantProfilesAdapter = createEntityAdapter<GrantProfile, string>({
  selectId: (profile) => profile.grant_profile_id,
  sortComparer: (left, right) =>
    right.updated_at.localeCompare(left.updated_at),
});

type GrantProfileRequestStatus = 'idle' | 'pending';

export interface GrantProfilesState {
  profiles: EntityState<GrantProfile, string>;
  listStatus: GrantProfileRequestStatus;
  /** A list read succeeded, so an absent profile is absent rather than unread. */
  listLoaded: boolean;
  detailStatus: GrantProfileRequestStatus;
  mutationStatus: GrantProfileRequestStatus;
  listError: ApiError;
  error: ApiError;
}

const initialState: GrantProfilesState = {
  profiles: grantProfilesAdapter.getInitialState(),
  listStatus: 'idle',
  listLoaded: false,
  detailStatus: 'idle',
  mutationStatus: 'idle',
  listError: ApiErrorInitialState,
  error: ApiErrorInitialState,
};

const getApiError = (error: unknown): ApiError => {
  const responseData = (error as { response?: { data?: ApiError } } | undefined)
    ?.response?.data;
  return (
    responseData ?? {
      error: error instanceof Error ? error.message : 'Unexpected API error',
      error_code: 0,
    }
  );
};

export const fetchGrantProfiles = createAsyncThunk<
  GrantProfile[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>('client/grantProfiles/fetchAll', async ({ api }, { rejectWithValue }) => {
  try {
    return (await api.get<GrantProfile[]>('/grant-profiles')).data;
  } catch (error) {
    return rejectWithValue(getApiError(error));
  }
});

export const fetchGrantProfile = createAsyncThunk<
  GrantProfile,
  { api: ClientApiV2; grantProfileId: string },
  { rejectValue: ApiError }
>(
  'client/grantProfiles/fetchOne',
  async ({ api, grantProfileId }, { rejectWithValue }) => {
    try {
      return (await api.get<GrantProfile>(`/grant-profiles/${grantProfileId}`))
        .data;
    } catch (error) {
      return rejectWithValue(getApiError(error));
    }
  },
);

export const createGrantProfile = createAsyncThunk<
  GrantProfile,
  { api: ClientApiV2; request: GrantProfileCreateRequest },
  { rejectValue: ApiError }
>(
  'client/grantProfiles/create',
  async ({ api, request }, { rejectWithValue }) => {
    try {
      return (await api.post<GrantProfile>('/grant-profiles', request)).data;
    } catch (error) {
      return rejectWithValue(getApiError(error));
    }
  },
);

export const updateGrantProfile = createAsyncThunk<
  GrantProfile,
  {
    api: ClientApiV2;
    grantProfileId: string;
    request: GrantProfilePatchRequest;
  },
  { rejectValue: ApiError }
>(
  'client/grantProfiles/update',
  async ({ api, grantProfileId, request }, { rejectWithValue }) => {
    try {
      return (
        await api.patch<GrantProfile>(
          `/grant-profiles/${grantProfileId}`,
          request,
        )
      ).data;
    } catch (error) {
      return rejectWithValue(getApiError(error));
    }
  },
);

export const deleteGrantProfile = createAsyncThunk<
  string,
  { api: ClientApiV2; grantProfileId: string },
  { rejectValue: ApiError }
>(
  'client/grantProfiles/delete',
  async ({ api, grantProfileId }, { rejectWithValue }) => {
    try {
      await api.delete(`/grant-profiles/${grantProfileId}`);
      return grantProfileId;
    } catch (error) {
      return rejectWithValue(getApiError(error));
    }
  },
);

const grantProfilesSlice = createSlice({
  name: 'grantProfiles',
  initialState,
  reducers: {
    clearGrantProfilesError(state) {
      state.listError = ApiErrorInitialState;
      state.error = ApiErrorInitialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGrantProfiles.pending, (state) => {
        state.listStatus = 'pending';
        state.listError = ApiErrorInitialState;
      })
      .addCase(fetchGrantProfiles.fulfilled, (state, { payload }) => {
        grantProfilesAdapter.setAll(state.profiles, payload);
        state.listStatus = 'idle';
        state.listLoaded = true;
        state.listError = ApiErrorInitialState;
        state.error = ApiErrorInitialState;
      })
      .addCase(fetchGrantProfiles.rejected, (state, { payload }) => {
        state.listStatus = 'idle';
        state.listError = payload ?? ApiErrorInitialState;
        state.error = state.listError;
      })
      .addCase(fetchGrantProfile.pending, (state) => {
        state.detailStatus = 'pending';
      })
      .addCase(fetchGrantProfile.fulfilled, (state, { payload }) => {
        grantProfilesAdapter.upsertOne(state.profiles, payload);
        state.detailStatus = 'idle';
        state.error = ApiErrorInitialState;
      })
      .addCase(fetchGrantProfile.rejected, (state, { payload }) => {
        state.detailStatus = 'idle';
        state.error = payload ?? ApiErrorInitialState;
      });

    builder
      .addCase(createGrantProfile.pending, (state) => {
        state.mutationStatus = 'pending';
      })
      .addCase(updateGrantProfile.pending, (state) => {
        state.mutationStatus = 'pending';
      })
      .addCase(deleteGrantProfile.pending, (state) => {
        state.mutationStatus = 'pending';
      });

    builder
      .addCase(createGrantProfile.fulfilled, (state, { payload }) => {
        grantProfilesAdapter.addOne(state.profiles, payload);
        state.mutationStatus = 'idle';
        state.error = ApiErrorInitialState;
      })
      .addCase(updateGrantProfile.fulfilled, (state, { payload }) => {
        grantProfilesAdapter.upsertOne(state.profiles, payload);
        state.mutationStatus = 'idle';
        state.error = ApiErrorInitialState;
      })
      .addCase(deleteGrantProfile.fulfilled, (state, { payload }) => {
        grantProfilesAdapter.removeOne(state.profiles, payload);
        state.mutationStatus = 'idle';
        state.error = ApiErrorInitialState;
      })
      .addCase(createGrantProfile.rejected, (state, { payload }) => {
        state.mutationStatus = 'idle';
        state.error = payload ?? ApiErrorInitialState;
      })
      .addCase(updateGrantProfile.rejected, (state, { payload }) => {
        state.mutationStatus = 'idle';
        state.error = payload ?? ApiErrorInitialState;
      })
      .addCase(deleteGrantProfile.rejected, (state, { payload }) => {
        state.mutationStatus = 'idle';
        state.error = payload ?? ApiErrorInitialState;
      });
  },
});

export const { clearGrantProfilesError } = grantProfilesSlice.actions;

export const {
  selectAll: selectAllGrantProfiles,
  selectById: selectGrantProfileById,
} = grantProfilesAdapter.getSelectors(
  (state: RootState) => state.grantProfiles.profiles,
);

export const selectGrantProfilesListLoading = (state: RootState) =>
  state.grantProfiles.listStatus === 'pending';

export const selectGrantProfilesListLoaded = (state: RootState) =>
  state.grantProfiles.listLoaded;

export const selectGrantProfileDetailLoading = (state: RootState) =>
  state.grantProfiles.detailStatus === 'pending';

export const selectGrantProfilesMutationLoading = (state: RootState) =>
  state.grantProfiles.mutationStatus === 'pending';

export const selectGrantProfilesError = (state: RootState) =>
  state.grantProfiles.error;

export const selectGrantProfilesListError = (state: RootState) =>
  state.grantProfiles.listError;

export const grantProfilesReducer = grantProfilesSlice.reducer;
